import { Slot, Slottable } from '@radix-ui/react-slot'
import clsx from 'clsx'
import React from 'react'

import { type IconProp } from '../../types'

export interface BaseLinkProps {
  children: React.ReactNode;
  color?: 'gray' | 'purple' | 'inherit';
  leftIcon?: IconProp;
  rightIcon?: IconProp;
  size?: 'normal' | 'small';
}

export function BaseLink ({
  children,
  color = 'gray',
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  ...props
}: BaseLinkProps) {
  const iconClassName = clsx(
    'inline-block align-text-bottom',
    'group-hover/link:text-[currentColor] group-active/link:text-[currentColor] group-focus-visible/link:text-[currentColor] group-disabled/link:text-[currentColor] group-aria-disabled/link:text-[currentColor]',
    'transition-colors duration-75',
    {
      'text-thatch-400': color === 'purple',
      'text-gray-400': color === 'gray',
      'text-inherit': color === 'inherit',
    }
  )

  function renderSlotChildren () {
    if (React.isValidElement(children)) {
      return React.cloneElement(
        children,
        undefined,
        (
          <span
            className='underline decoration-dotted group-hover/link:decoration-solid group-disabled/link:group-hover/link:decoration-dotted group-aria-disabled/link:group-hover/link:decoration-dotted underline-offset-[0.25rem] transition'
          >
            {children.props.children}
          </span>
        )
      )
    }
  }

  return (
    <Slot
      {...props}
      className={clsx(
        'appearance-none',
        'group/link',
        'rounded-sm',
        'inline',
        'font-medium text-left',
        'outline outline-offset-4 outline-2 outline-transparent focus-visible:outline-thatch-500',
        'aria-disabled:pointer-events-none',
        'transition-colors',
        {
          'text-thatch-500 hover:text-thatch-700 focus-visible:text-thatch-700 disabled:text-thatch-300 aria-disabled:text-thatch-300':
            color === 'purple',
          'text-gray-500 hover:text-gray-700 focus-visible:text-gray-700 disabled:text-gray-300 aria-disabled:text-gray-300':
            color === 'gray',
          'text-inherit opacity-95 hover:text-inherit hover:opacity-100 focus-visible:text-inherit focus-visible:opacity-100 disabled:text-inherit disabled:opacity-60 aria-disabled:text-inherit aria-disabled:opacity-60': color === 'inherit',
        }
      )}
    >
      {LeftIcon && (
        <span className={clsx(iconClassName, 'mr-1')}>
          <LeftIcon size={16} />
        </span>
      )}
      <Slottable>{renderSlotChildren()}</Slottable>
      {RightIcon && (
        <span className={clsx(iconClassName, 'ml-1')}>
          <RightIcon size={16} />
        </span>
      )}
    </Slot>
  )
}
